import { useState } from 'react';
import {
  TrashIcon,
  PlayIcon,
  GlobeAltIcon,
  LockClosedIcon,
  ArchiveBoxIcon,
  ArchiveBoxArrowDownIcon,
} from '@heroicons/react/24/outline';
import { Card, CardContent, Badge, Button, VideoThumbnail, Checkbox } from '@/components/ui';
import { formatDate } from '@/utils/formatters';
import { useMediaUrl } from '@/hooks/useMediaUrl';
import type { MovementTemplate } from '@/types/api';
import type { AnnotationTemplate } from '@/services/templateApi';
import { cn } from '@/utils/cn';
import { normalizeTemplate, getTemplateMovementDescription } from '@/utils/templateHelpers';
import {
  useUpdateTemplateVisibility,
  useArchiveTemplate,
  useUnarchiveTemplate,
} from '@/hooks/useTemplates';

// TemplateCard is fed both template shapes by real callers (ChannelPage,
// SelectTemplateForEvaluation, TemplateLibrary pass AnnotationTemplate from
// services/templateApi.ts; others pass MovementTemplate from types/api.ts).
// It only reads the fields the two share (id, visibility, is_archived) plus
// whatever normalizeTemplate() extracts, so the union is the honest type —
// narrowing to just one would be structurally false for real call sites.
type TemplateCardTemplate = MovementTemplate | AnnotationTemplate;

interface TemplateCardProps {
  template: TemplateCardTemplate;
  onSelect?: (template: TemplateCardTemplate) => void;
  onDelete?: (templateId: string) => void;
  onEvaluate?: (templateId: string) => void;
  selected?: boolean;
  className?: string;
  // Bulk selection support
  selectable?: boolean;
  onToggleSelect?: (id: string) => void;
  /** When true, show the visibility toggle button (for coach/admin users). */
  canEditVisibility?: boolean;
  /** When true, show the archive/unarchive button (for coach/admin users). */
  canArchive?: boolean;
}

function TemplateCard({
  template,
  onSelect,
  onDelete,
  onEvaluate,
  selected,
  className,
  selectable,
  onToggleSelect,
  canEditVisibility = false,
  canArchive = false,
}: TemplateCardProps) {
  // Normalize template to handle API inconsistencies
  const normalized = normalizeTemplate(template);

  // Visibility state — optimistically updated on toggle.
  const [currentVisibility, setCurrentVisibility] = useState<'private' | 'public'>(
    template.visibility ?? 'private',
  );
  const [showPublicConfirm, setShowPublicConfirm] = useState(false);
  const { mutate: updateVisibility, isPending: isUpdatingVisibility } =
    useUpdateTemplateVisibility();

  // Archive state (D12).
  const isArchived = template.is_archived ?? false;
  const [archiveError, setArchiveError] = useState<string | null>(null);
  const { mutate: archiveTpl, isPending: isArchiving } = useArchiveTemplate();
  const { mutate: unarchiveTpl, isPending: isUnarchiving } = useUnarchiveTemplate();

  const handleArchiveToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setArchiveError(null);
    if (isArchived) {
      unarchiveTpl(normalized.id, {
        onError: (err: unknown) => {
          // 409 → active-template cap reached.
          const detail = (err as { response?: { data?: { detail?: { message?: string } } } })
            ?.response?.data?.detail;
          setArchiveError(
            detail?.message ??
              'Could not un-archive — you may be at your active-template limit.',
          );
        },
      });
    } else {
      archiveTpl(normalized.id);
    }
  };

  // Generate thumbnail URL - use trim_start_frame for the preview.
  // Media-auth security sprint (2026-09-21): /videos/{id}/thumbnail now
  // requires a bearer token or a signed sig/exp pair — useMediaUrl mints and
  // refreshes the signature. Resolves to null while the token is loading.
  const { thumbnailUrl: buildThumbnailUrl } = useMediaUrl(normalized.referenceVideoId || undefined);
  const thumbnailUrl = normalized.referenceVideoId
    ? buildThumbnailUrl(normalized.trimStartFrame || 0, 320) ?? ''
    : '';

  const handleVisibilityToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (currentVisibility === 'private') {
      // Going public — show confirmation modal first.
      setShowPublicConfirm(true);
    } else {
      // Going private — no confirmation needed.
      doUpdateVisibility('private');
    }
  };

  const doUpdateVisibility = (newVisibility: 'private' | 'public') => {
    updateVisibility(
      { id: normalized.id, visibility: newVisibility },
      {
        onSuccess: () => {
          setCurrentVisibility(newVisibility);
        },
      },
    );
    setShowPublicConfirm(false);
  };

  return (
    <>
      <Card
        className={cn(
          'overflow-hidden transition-all bg-dark-card border border-border-subtle rounded-card hover:border-border-strong hover:shadow-glow-green cursor-pointer relative',
          selected && 'ring-2 ring-accent',
          className,
        )}
        onClick={() => onSelect?.(template)}
      >
        {/* Bulk selection checkbox — top-left corner, absolutely positioned */}
        {selectable && (
          <div
            className="absolute top-2 left-2 z-10"
            onClick={(e) => e.stopPropagation()}
          >
            <Checkbox
              checked={selected ?? false}
              onChange={() => onToggleSelect?.(normalized.id)}
            />
          </div>
        )}

        {/* Video thumbnail with loading/error fallback */}
        <VideoThumbnail
          src={thumbnailUrl}
          alt={`${normalized.name} preview`}
          className="aspect-video"
        />

        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <h3 className="font-medium text-text-primary truncate">{normalized.name}</h3>
              {normalized.description && (
                <p className="text-sm text-text-secondary mt-1 line-clamp-2">
                  {normalized.description}
                </p>
              )}
              <p className="text-xs text-text-muted mt-1">
                {getTemplateMovementDescription(normalized)}
              </p>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <Badge variant="secondary" size="sm">
                  {normalized.phases.length} phases
                </Badge>
                {normalized.regions.length > 0 && (
                  <span className="text-xs text-text-muted">
                    {normalized.regions.length} regions
                  </span>
                )}
                {/* Visibility badge (Security P2-1) */}
                {currentVisibility === 'public' ? (
                  <Badge variant="warning" size="sm" title="Anyone can view this template">
                    <GlobeAltIcon className="h-3 w-3 mr-1 inline" />
                    Public
                  </Badge>
                ) : (
                  <Badge variant="secondary" size="sm" title="Only you and collaborators can view this template">
                    <LockClosedIcon className="h-3 w-3 mr-1 inline" />
                    Private
                  </Badge>
                )}
                {/* Archived badge (D12) */}
                {isArchived && (
                  <Badge variant="warning" size="sm" title="Archived — excluded from your active-template limit">
                    <ArchiveBoxIcon className="h-3 w-3 mr-1 inline" />
                    Archived
                  </Badge>
                )}
              </div>
              {normalized.createdAt && (
                <p className="text-xs text-text-muted mt-2">{formatDate(normalized.createdAt)}</p>
              )}
            </div>
            {onDelete && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(normalized.id);
                }}
                className="text-text-muted hover:text-danger transition-colors"
                aria-label="Delete template"
              >
                <TrashIcon className="h-5 w-5" />
              </button>
            )}
          </div>

          {/* Visibility + archive toggles — only shown to users with write access */}
          {(canEditVisibility || canArchive) && (
            <div className="mt-3 pt-3 border-t border-border-subtle flex items-center gap-4 flex-wrap">
              {canEditVisibility && (
                <button
                  onClick={handleVisibilityToggle}
                  disabled={isUpdatingVisibility}
                  className={cn(
                    'text-xs flex items-center gap-1 transition-colors disabled:opacity-50',
                    currentVisibility === 'public'
                      ? 'text-warning hover:text-warning/80'
                      : 'text-text-muted hover:text-text-secondary',
                  )}
                  aria-label={
                    currentVisibility === 'public'
                      ? 'Make template private'
                      : 'Make template public'
                  }
                >
                  {currentVisibility === 'public' ? (
                    <>
                      <LockClosedIcon className="h-3.5 w-3.5" />
                      Make private
                    </>
                  ) : (
                    <>
                      <GlobeAltIcon className="h-3.5 w-3.5" />
                      Make public
                    </>
                  )}
                </button>
              )}
              {canArchive && (
                <button
                  onClick={handleArchiveToggle}
                  disabled={isArchiving || isUnarchiving}
                  className="text-xs flex items-center gap-1 text-text-muted hover:text-text-secondary transition-colors disabled:opacity-50"
                  aria-label={isArchived ? 'Un-archive template' : 'Archive template'}
                >
                  {isArchived ? (
                    <>
                      <ArchiveBoxArrowDownIcon className="h-3.5 w-3.5" />
                      Un-archive
                    </>
                  ) : (
                    <>
                      <ArchiveBoxIcon className="h-3.5 w-3.5" />
                      Archive
                    </>
                  )}
                </button>
              )}
            </div>
          )}
          {archiveError && (
            <p className="mt-2 text-xs text-danger" role="alert">
              {archiveError}
            </p>
          )}

          {/* Action buttons */}
          {onEvaluate && (
            <div className="mt-4 pt-4 border-t border-border-subtle">
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  onEvaluate(normalized.id);
                }}
                variant="secondary"
                size="sm"
                className="w-full"
              >
                <PlayIcon className="h-4 w-4 mr-2" />
                Evaluate Performance
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Confirmation modal: making a template public */}
      {showPublicConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={(e) => {
            e.stopPropagation();
            setShowPublicConfirm(false);
          }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="visibility-confirm-title"
        >
          <div
            className="bg-dark-card border border-border-subtle rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <GlobeAltIcon className="h-6 w-6 text-warning shrink-0" />
              <h2 id="visibility-confirm-title" className="text-lg font-semibold text-text-primary">
                Make template public?
              </h2>
            </div>
            <p className="text-sm text-text-secondary mb-6">
              Anyone with the link — including visitors who are not signed in — will
              be able to view this template&rsquo;s full contents, including phases,
              annotations, and coach notes. Make sure no proprietary content is included.
            </p>
            <div className="flex gap-3 justify-end">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowPublicConfirm(false)}
              >
                Keep private
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => doUpdateVisibility('public')}
                disabled={isUpdatingVisibility}
                className="bg-warning text-dark-base hover:bg-warning/90"
              >
                <GlobeAltIcon className="h-4 w-4 mr-1" />
                Make public
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default TemplateCard;
